import React, { useEffect, useState, useRef, useMemo } from "react";
import AdminSidebar from "../components/AdminSidebar";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, Upload, EyeOff, Eye, BookOpen, Search, Check, ChefHat, FileText, Sparkles, Loader2, AlertTriangle } from "lucide-react";

export default function AdminMenu() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const [importing, setImporting] = useState(false);
    const [parsingFile, setParsingFile] = useState(false);

    const load = () => api.get("/admin/menu-items").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const del = async (id) => {
        if (!window.confirm("Eliminare questo piatto?")) return;
        try { await api.delete(`/admin/menu-items/${id}`); toast.success("Eliminato"); load(); }
        catch { toast.error("Errore"); }
    };

    const toggle = async (it) => {
        try {
            await api.put(`/admin/menu-items/${it.menu_item_id}`, { available: !it.available });
            load();
        } catch { toast.error("Errore"); }
    };

    const grouped = items.reduce((acc, it) => {
        (acc[it.category || "Altro"] ||= []).push(it);
        return acc;
    }, {});

    return (
        <div className="flex min-h-screen bg-ink" data-testid="admin-menu-page">
            <AdminSidebar />
            <main className="flex-1 p-10">
                <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <div className="text-gold text-xs tracking-[0.4em] uppercase">Gestione</div>
                        <h1 className="font-serif text-5xl text-white mt-2">Menù</h1>
                        <p className="text-gray-400 text-sm mt-2">Carica i tuoi piatti dal catalogo Renova oppure creane di tuoi. I clienti possono pre-ordinare durante la prenotazione.</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button onClick={() => setParsingFile(true)} data-testid="ai-parse-menu-btn"
                            className="inline-flex items-center gap-2 bg-violet-500 text-white font-semibold px-5 py-3 rounded-sm hover:bg-violet-400 shadow-[0_0_20px_rgba(139,92,246,0.25)]">
                            <Sparkles size={16} /> Carica il tuo menù (AI)
                        </button>
                        <button onClick={() => setImporting(true)} data-testid="import-recipes-btn"
                            className="inline-flex items-center gap-2 bg-emerald-500 text-black font-semibold px-5 py-3 rounded-sm hover:bg-emerald-400">
                            <BookOpen size={16} /> Carica dal catalogo Renova
                        </button>
                        <button onClick={() => setCreating(true)} data-testid="add-menu-item-btn"
                            className="inline-flex items-center gap-2 bg-gold text-black font-semibold px-5 py-3 rounded-sm hover:bg-gold-light">
                            <Plus size={16} /> Crea piatto
                        </button>
                    </div>
                </div>

                {items.length === 0 ? (
                    <div className="bg-ink-surface border border-white/10 rounded-sm p-12 text-center" data-testid="menu-empty-state">
                        <ChefHat size={42} className="text-gold mx-auto mb-4" strokeWidth={1.2} />
                        <p className="text-gray-300 font-serif text-2xl mb-2">Costruisci il tuo menù in 30 secondi</p>
                        <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">Importa i piatti dal catalogo Renova (220 ricette tradizionali italiane già pronte) oppure parti da zero creando i tuoi.</p>
                        <div className="flex gap-2 justify-center flex-wrap">
                            <button onClick={() => setParsingFile(true)} className="bg-violet-500 text-white px-6 py-3 rounded-sm font-semibold inline-flex items-center gap-2">
                                <Sparkles size={16} /> Carica il tuo menù (AI)
                            </button>
                            <button onClick={() => setImporting(true)} className="bg-emerald-500 text-black px-6 py-3 rounded-sm font-semibold inline-flex items-center gap-2">
                                <BookOpen size={16} /> Sfoglia il catalogo Renova
                            </button>
                            <button onClick={() => setCreating(true)} className="bg-gold text-black px-6 py-3 rounded-sm font-semibold inline-flex items-center gap-2">
                                <Plus size={16} /> Crea piatto manuale
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-10">
                        {Object.entries(grouped).map(([cat, arr]) => (
                            <div key={cat}>
                                <h2 className="font-serif text-2xl text-gold mb-4">{cat}</h2>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {arr.map((it) => (
                                        <div key={it.menu_item_id} className={`bg-ink-surface border rounded-sm overflow-hidden transition-colors ${it.available ? "border-white/10 hover:border-gold/40" : "border-white/10 opacity-60"}`} data-testid={`menu-item-${it.menu_item_id}`}>
                                            {it.photo_path && (
                                                <div className="aspect-[16/10] overflow-hidden bg-ink">
                                                    <img src={`${API}/files/${it.photo_path}`} alt={it.name} className="w-full h-full object-cover" />
                                                </div>
                                            )}
                                            <div className="p-5">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="font-serif text-xl text-white truncate">{it.name}</h3>
                                                        {it.description && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{it.description}</p>}
                                                    </div>
                                                    <div className="text-gold font-semibold whitespace-nowrap">€ {(it.price_cents / 100).toFixed(2)}</div>
                                                </div>
                                                <div className="flex gap-1 mt-4 pt-4 border-t border-white/5">
                                                    <button onClick={() => toggle(it)} className="flex-1 text-xs text-gray-400 hover:text-gold py-1.5 inline-flex items-center justify-center gap-1" data-testid={`toggle-${it.menu_item_id}`}>
                                                        {it.available ? <><Eye size={12} /> Disponibile</> : <><EyeOff size={12} /> Nascosto</>}
                                                    </button>
                                                    <button onClick={() => setEditing(it)} className="p-2 text-gray-400 hover:text-gold" data-testid={`edit-${it.menu_item_id}`}>
                                                        <Edit3 size={13} strokeWidth={1.5} />
                                                    </button>
                                                    <button onClick={() => del(it.menu_item_id)} className="p-2 text-gray-400 hover:text-red-400" data-testid={`delete-${it.menu_item_id}`}>
                                                        <Trash2 size={13} strokeWidth={1.5} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {(editing || creating) && (
                    <MenuItemDialog
                        item={editing}
                        onClose={() => { setEditing(null); setCreating(false); }}
                        onSaved={() => { load(); setEditing(null); setCreating(false); }}
                    />
                )}

                {importing && (
                    <RecipeCatalogDialog
                        onClose={() => setImporting(false)}
                        onImported={() => { load(); setImporting(false); }}
                    />
                )}

                {parsingFile && (
                    <AIMenuParserDialog
                        onClose={() => setParsingFile(false)}
                        onImported={() => { load(); setParsingFile(false); }}
                    />
                )}
            </main>
        </div>
    );
}

function RecipeCatalogDialog({ onClose, onImported }) {
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [activeCategory, setActiveCategory] = useState("Tutti");
    const [selected, setSelected] = useState(new Set());
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get("/admin/recipe-catalog").then((r) => setRecipes(r.data)).finally(() => setLoading(false));
    }, []);

    const categories = useMemo(() => ["Tutti", ...Array.from(new Set(recipes.map((r) => r.category)))], [recipes]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return recipes.filter((r) => {
            if (activeCategory !== "Tutti" && r.category !== activeCategory) return false;
            if (q && !r.name.toLowerCase().includes(q) && !r.description.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [recipes, search, activeCategory]);

    const toggle = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectAll = () => {
        const next = new Set(selected);
        filtered.forEach((r) => next.add(r.recipe_id));
        setSelected(next);
    };

    const clearAll = () => setSelected(new Set());

    const submit = async () => {
        if (selected.size === 0) { toast.error("Seleziona almeno un piatto"); return; }
        setSubmitting(true);
        try {
            const r = await api.post("/admin/menu-items/import", { recipe_ids: Array.from(selected) });
            const { imported, skipped_duplicates } = r.data;
            if (imported > 0) {
                toast.success(`${imported} piatti aggiunti al menù${skipped_duplicates ? ` · ${skipped_duplicates} già presenti saltati` : ""}`);
            } else if (skipped_duplicates > 0) {
                toast.info(`Tutti i ${skipped_duplicates} piatti erano già nel tuo menù`);
            }
            onImported();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore importazione");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={onClose} data-testid="recipe-catalog-dialog">
            <div className="bg-ink-surface border border-white/10 rounded-sm w-full max-w-5xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="p-6 border-b border-white/10 flex items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-emerald-400 text-xs tracking-[0.4em] uppercase">
                            <BookOpen size={14} /> Catalogo Renova
                        </div>
                        <h3 className="font-serif text-2xl text-white mt-1">220 ricette tradizionali italiane</h3>
                        <p className="text-xs text-gray-400 mt-1">Seleziona i piatti che vuoi importare nel tuo menù. I prezzi sono solo suggeriti — potrai modificarli dopo.</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-white shrink-0" data-testid="close-catalog-btn"><X size={20} /></button>
                </div>

                {/* Filters */}
                <div className="px-6 pt-4 flex flex-wrap gap-3 items-center">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Cerca un piatto..."
                            data-testid="catalog-search-input"
                            className="w-full pl-9 pr-3 py-2 bg-black/30 border border-white/15 rounded-sm text-white placeholder-gray-500 focus:border-emerald-400 focus:outline-none text-sm"
                        />
                    </div>
                    <button onClick={selectAll} className="text-[11px] uppercase tracking-widest text-emerald-300 hover:text-emerald-200 px-3 py-1.5 border border-emerald-500/30 rounded-sm" data-testid="catalog-select-all-btn">
                        Seleziona tutti i visibili
                    </button>
                    <button onClick={clearAll} className="text-[11px] uppercase tracking-widest text-gray-400 hover:text-white px-3 py-1.5 border border-white/15 rounded-sm" data-testid="catalog-clear-btn">
                        Pulisci selezione
                    </button>
                </div>

                <div className="px-6 pt-3 flex flex-wrap gap-1.5">
                    {categories.map((c) => (
                        <button
                            key={c}
                            onClick={() => setActiveCategory(c)}
                            className={`text-[11px] px-3 py-1.5 rounded-sm border tracking-widest uppercase ${
                                activeCategory === c ? "bg-emerald-500 text-black border-emerald-500" : "border-white/15 text-gray-300 hover:border-emerald-500/40"
                            }`}
                            data-testid={`catalog-cat-${c.replace(/\s+/g, "-")}`}
                        >
                            {c}
                        </button>
                    ))}
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading ? (
                        <div className="text-center text-gray-400 py-12">Carico catalogo...</div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center text-gray-500 py-12 text-sm">Nessuna ricetta trovata.</div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {filtered.map((r) => {
                                const isSel = selected.has(r.recipe_id);
                                return (
                                    <button
                                        key={r.recipe_id}
                                        type="button"
                                        onClick={() => toggle(r.recipe_id)}
                                        data-testid={`catalog-recipe-${r.recipe_id}`}
                                        className={`text-left p-3 rounded-sm border transition-colors ${
                                            isSel
                                                ? "border-emerald-500 bg-emerald-500/[0.07]"
                                                : "border-white/10 hover:border-emerald-500/40"
                                        }`}
                                    >
                                        <div className="flex items-start gap-2">
                                            <div className={`mt-0.5 w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${
                                                isSel ? "bg-emerald-500 border-emerald-500" : "border-white/30"
                                            }`}>
                                                {isSel && <Check size={11} className="text-black" strokeWidth={3} />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-baseline justify-between gap-2">
                                                    <h4 className="text-sm font-semibold text-white truncate">{r.name}</h4>
                                                    <span className="text-[11px] text-gold whitespace-nowrap">€ {(r.suggested_price_cents / 100).toFixed(2)}</span>
                                                </div>
                                                <div className="text-[10px] text-gray-500 uppercase tracking-widest mt-0.5">{r.category}</div>
                                                {r.description && <p className="text-[11px] text-gray-400 mt-1 line-clamp-2">{r.description}</p>}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-white/10 flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs text-gray-400" data-testid="catalog-counter">
                        <span className="text-emerald-300 font-semibold">{selected.size}</span> selezionati su {filtered.length} visibili ({recipes.length} totali)
                    </div>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-widest text-gray-400 hover:text-white">Annulla</button>
                        <button
                            onClick={submit}
                            disabled={submitting || selected.size === 0}
                            data-testid="catalog-import-btn"
                            className="bg-emerald-500 text-black font-semibold px-5 py-2.5 rounded-sm hover:bg-emerald-400 tracking-widest uppercase text-xs disabled:opacity-50"
                        >
                            {submitting ? "Importo..." : `Aggiungi ${selected.size} al mio menù`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function MenuItemDialog({ item, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: item?.name || "",
        description: item?.description || "",
        price_eur: item ? (item.price_cents / 100).toFixed(2) : "",
        category: item?.category || "Piatti",
        photo_path: item?.photo_path || null,
        available: item?.available ?? true,
        sort_order: item?.sort_order || 0,
    });
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const upload = async (file) => {
        setUploading(true);
        try {
            const data = new FormData();
            data.append("file", file);
            const r = await api.post("/admin/upload-photo", data, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((f) => ({ ...f, photo_path: r.data.path }));
            toast.success("Foto caricata");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload fallito");
        } finally {
            setUploading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        const priceCents = Math.round(parseFloat(form.price_eur) * 100);
        if (isNaN(priceCents) || priceCents < 0) { toast.error("Prezzo non valido"); return; }
        try {
            const payload = {
                name: form.name, description: form.description, category: form.category,
                price_cents: priceCents, photo_path: form.photo_path,
                available: form.available, sort_order: parseInt(form.sort_order, 10) || 0,
            };
            if (item) await api.put(`/admin/menu-items/${item.menu_item_id}`, payload);
            else await api.post("/admin/menu-items", payload);
            toast.success("Salvato");
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 overflow-y-auto" onClick={onClose}>
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
                className="bg-ink-surface border border-white/10 rounded-sm p-8 max-w-lg w-full space-y-4 my-8" data-testid="menu-item-dialog">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-serif text-2xl text-white">{item ? "Modifica piatto" : "Nuovo piatto"}</h3>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
                </div>

                <Field label="Nome">
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid="menu-name-input" className={inputCls} placeholder="Es. Carbonara" />
                </Field>
                <Field label="Descrizione">
                    <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        data-testid="menu-description-input" className={inputCls + " resize-none"}
                        placeholder="Guanciale croccante, pecorino romano, uovo, pepe nero..." />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Prezzo (€)">
                        <input required type="number" step="0.01" min="0" value={form.price_eur}
                            onChange={(e) => setForm({ ...form, price_eur: e.target.value })}
                            data-testid="menu-price-input" className={inputCls} placeholder="15.00" />
                    </Field>
                    <Field label="Categoria">
                        <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                            data-testid="menu-category-input" className={inputCls} placeholder="Primi" />
                    </Field>
                </div>

                <Field label="Foto (opzionale)">
                    <div className="space-y-2">
                        {form.photo_path && (
                            <div className="relative w-full aspect-[16/10] overflow-hidden rounded-sm bg-ink">
                                <img src={`${API}/files/${form.photo_path}`} alt="" className="w-full h-full object-cover" />
                                <button type="button" onClick={() => setForm({ ...form, photo_path: null })}
                                    className="absolute top-2 right-2 bg-black/70 text-white p-1.5 rounded-sm hover:bg-red-500/80" data-testid="remove-photo-btn">
                                    <X size={14} />
                                </button>
                            </div>
                        )}
                        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                            onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="photo-file-input" />
                        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                            data-testid="upload-photo-btn"
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-dashed border-white/20 rounded-sm text-gray-400 hover:text-gold hover:border-gold/50 text-sm">
                            <Upload size={14} /> {uploading ? "Caricamento..." : (form.photo_path ? "Sostituisci foto" : "Carica foto (jpg/png, max 5MB)")}
                        </button>
                    </div>
                </Field>

                <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })}
                        data-testid="menu-available-input" className="w-4 h-4 accent-gold" />
                    <span className="text-sm text-gray-300">Disponibile per i clienti</span>
                </label>

                <button type="submit" data-testid="menu-save-btn"
                    className="w-full bg-gold text-black font-semibold py-3 rounded-sm hover:bg-gold-light tracking-widest uppercase text-sm">
                    Salva
                </button>
            </form>
        </div>
    );
}

const inputCls = "w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";
function Field({ label, children }) {
    return <label className="block"><span className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold">{label}</span><div className="mt-2">{children}</div></label>;
}


const CATEGORIES_ORDER = ["Antipasti", "Primi", "Secondi", "Contorni", "Pizze", "Insalate", "Dolci", "Bevande", "Vini", "Altro"];

/**
 * Upload a menu photo / PDF, let the backend AI-parse it, then let the
 * restaurateur review, edit and bulk-insert the dishes. The preview is kept
 * fully editable so they can correct anything the model got wrong before
 * committing.
 */
function AIMenuParserDialog({ onClose, onImported }) {
    const [file, setFile] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [drafts, setDrafts] = useState(null);
    const [replaceAll, setReplaceAll] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [quota, setQuota] = useState(null);
    const [buyingPack, setBuyingPack] = useState(false);
    const inputRef = useRef(null);

    // Load current-month quota on open
    useEffect(() => {
        api.get("/admin/menu-items/parse-quota").then((r) => setQuota(r.data)).catch(() => {});
    }, []);

    const buyPack = async () => {
        setBuyingPack(true);
        try {
            const { data } = await api.post("/admin/menu-items/parse-topup/checkout", {
                origin_url: window.location.origin,
            });
            if (data.url) window.location.href = data.url;
            else toast.error("Stripe Checkout non disponibile");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore Stripe");
        } finally {
            setBuyingPack(false);
        }
    };

    const handleFile = (f) => {
        if (!f) return;
        const allowed = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
        if (!allowed.includes(f.type) && !f.name.toLowerCase().endsWith(".pdf") && !f.name.toLowerCase().endsWith(".heic")) {
            toast.error("Formato non supportato. Usa PDF, JPG, PNG, WEBP o HEIC.");
            return;
        }
        if (f.size > 12 * 1024 * 1024) {
            toast.error("File troppo grande (max 12MB).");
            return;
        }
        setFile(f);
    };

    const runParse = async () => {
        if (!file) return;
        setParsing(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/menu-items/parse", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            if (!data.items?.length) {
                toast.error("Nessun piatto rilevato. Prova con un'immagine più nitida.");
                return;
            }
            setDrafts(data.items);
            if (data.usage) setQuota(data.usage);
            toast.success(`Rilevati ${data.items.length} piatti in ${data.pages} pagina/e`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore durante l'analisi");
            // Refresh quota so the user immediately sees the credit was NOT
            // consumed (backend only consumes on full success).
            api.get("/admin/menu-items/parse-quota").then((r) => setQuota(r.data)).catch(() => {});
        } finally {
            setParsing(false);
        }
    };

    const updateDraft = (idx, patch) => {
        setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
    };
    const removeDraft = (idx) => setDrafts((prev) => prev.filter((_, i) => i !== idx));

    const confirm = async () => {
        const valid = drafts.filter((d) => d.name.trim());
        if (!valid.length) { toast.error("Nessun piatto valido"); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/menu-items/bulk-create", {
                items: valid.map((d) => ({
                    name: d.name.trim(),
                    description: (d.description || "").trim(),
                    price_cents: parseInt(d.price_cents, 10) || 0,
                    category: d.category || "Altro",
                })),
                replace_all: replaceAll,
            });
            toast.success(`${data.imported} piatti importati${data.skipped_duplicates ? ` · ${data.skipped_duplicates} duplicati saltati` : ""}`);
            onImported();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore durante il salvataggio");
        } finally {
            setSubmitting(false);
        }
    };

    const groupedDrafts = useMemo(() => {
        if (!drafts) return {};
        const out = {};
        for (const d of drafts) (out[d.category || "Altro"] ||= []).push(d);
        return out;
    }, [drafts]);

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 animate-fade-up" onClick={onClose} data-testid="ai-parse-dialog">
            <div className="bg-ink-surface border border-violet-400/40 rounded-sm w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between p-6 border-b border-white/10">
                    <div className="flex items-start gap-3">
                        <div className="w-11 h-11 rounded-sm bg-violet-500/15 border border-violet-400/40 flex items-center justify-center">
                            <Sparkles size={20} className="text-violet-300" strokeWidth={1.6} />
                        </div>
                        <div>
                            <div className="text-[10px] tracking-[0.3em] uppercase text-violet-300 font-semibold">AI Menu Import</div>
                            <h2 className="font-serif text-3xl text-white mt-0.5">Carica il tuo menù</h2>
                            <p className="text-gray-400 text-sm mt-1 max-w-xl">Carica un PDF o una foto del tuo menù. L'AI estrae piatti, descrizioni e prezzi e li distribuisce nelle categorie corrette.</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="ai-parse-close"><X size={20} /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {!drafts && (
                        <>
                            {quota && (
                                <div className="mb-4 bg-ink border border-violet-400/20 rounded-sm p-3.5" data-testid="ai-parse-quota-badge">
                                    <div className="flex items-center justify-between gap-3 flex-wrap text-xs">
                                        <div className="flex items-center gap-2 text-gray-300">
                                            <Sparkles size={13} className="text-violet-300" strokeWidth={1.7} />
                                            <span>
                                                Disponibili: <strong className="text-white">{quota.total_remaining}</strong>
                                                <span className="text-gray-500"> · {quota.monthly_remaining}/{quota.limit} gratuiti questo mese</span>
                                                {quota.bank > 0 && <span className="text-emerald-300"> · {quota.bank} dal pacchetto</span>}
                                            </span>
                                        </div>
                                        <span className="text-gray-500">Gratuiti si azzerano il {quota.reset_on}</span>
                                    </div>
                                </div>
                            )}
                            {quota && quota.total_remaining === 0 && (
                                <div className="bg-gradient-to-br from-violet-500/10 to-gold/10 border border-violet-400/40 rounded-sm p-5 mb-4" data-testid="ai-parse-quota-exhausted">
                                    <div className="flex items-start gap-3 mb-3">
                                        <AlertTriangle size={20} className="text-violet-300 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-white font-semibold text-sm">Analisi AI esaurite</div>
                                            <div className="text-xs text-gray-300 mt-1 leading-relaxed">
                                                Hai usato tutti i {quota.limit} upload gratuiti di questo mese. Acquista un pacchetto da <strong className="text-gold">{quota.pack_credits} upload a €{(quota.pack_price_cents / 100).toFixed(0)}</strong> — <strong>non scadono mai</strong>. Oppure attendi il {quota.reset_on}.
                                            </div>
                                        </div>
                                    </div>
                                    <button onClick={buyPack} disabled={buyingPack} data-testid="ai-parse-buy-pack-btn"
                                        className="w-full bg-gold text-black font-semibold py-3 rounded-sm hover:bg-gold-light disabled:opacity-50 tracking-widest uppercase text-xs inline-flex items-center justify-center gap-2">
                                        {buyingPack ? <><Loader2 size={14} className="animate-spin" /> Reindirizzamento…</> : <>Acquista {quota.pack_credits} upload · €{(quota.pack_price_cents / 100).toFixed(0)}</>}
                                    </button>
                                </div>
                            )}
                            <div className="border-2 border-dashed border-white/15 hover:border-violet-400/60 rounded-sm p-10 text-center cursor-pointer transition-colors"
                                onClick={() => inputRef.current?.click()}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
                                data-testid="ai-parse-dropzone">
                                <FileText size={36} className="text-violet-300 mx-auto mb-3" strokeWidth={1.2} />
                                <p className="text-white font-serif text-xl mb-1">{file ? file.name : "Trascina qui il tuo menù"}</p>
                                <p className="text-xs text-gray-500">{file ? `${(file.size / 1024).toFixed(0)} KB` : "PDF · JPG · PNG · WEBP · HEIC · max 12MB"}</p>
                                <input ref={inputRef} type="file" accept="application/pdf,image/*,.pdf,.heic,.heif" hidden
                                    onChange={(e) => handleFile(e.target.files?.[0])} data-testid="ai-parse-file-input" />
                            </div>

                            <div className="bg-violet-500/5 border border-violet-400/20 rounded-sm p-4 mt-5 flex gap-3">
                                <AlertTriangle size={16} className="text-violet-300 shrink-0 mt-0.5" strokeWidth={1.6} />
                                <div className="text-xs text-gray-300 leading-relaxed">
                                    <strong className="text-violet-200">Suggerimento:</strong> per il miglior risultato usa un menù chiaro, ben illuminato, con prezzi leggibili. L'analisi richiede ~10-30 secondi. Potrai <strong>rivedere ed editare ogni piatto</strong> prima del salvataggio.
                                </div>
                            </div>

                            {file && (
                                <button onClick={runParse} disabled={parsing || (quota && quota.total_remaining <= 0)} data-testid="ai-parse-run-btn"
                                    className="mt-5 w-full bg-violet-500 text-white font-semibold py-3.5 rounded-sm hover:bg-violet-400 disabled:opacity-50 disabled:cursor-not-allowed tracking-widest uppercase text-sm inline-flex items-center justify-center gap-2">
                                    {parsing ? <><Loader2 size={16} className="animate-spin" /> Analisi in corso…</> : <><Sparkles size={16} /> Analizza con AI</>}
                                </button>
                            )}
                        </>
                    )}

                    {drafts && (
                        <>
                            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                                <div className="text-sm text-gray-300">
                                    <span className="text-white font-semibold">{drafts.length}</span> piatti rilevati. Rivedi, correggi e conferma.
                                </div>
                                <label className="inline-flex items-center gap-2 text-xs text-gray-400 cursor-pointer" data-testid="ai-parse-replace-toggle">
                                    <input type="checkbox" checked={replaceAll} onChange={(e) => setReplaceAll(e.target.checked)} className="w-4 h-4 accent-violet-400" />
                                    Sostituisci TUTTO il menù esistente (pericoloso)
                                </label>
                            </div>

                            <div className="space-y-6">
                                {CATEGORIES_ORDER.filter((c) => groupedDrafts[c]).map((cat) => (
                                    <div key={cat}>
                                        <h3 className="font-serif text-xl text-gold mb-2">{cat} <span className="text-[11px] text-gray-500 tracking-widest uppercase ml-2">{groupedDrafts[cat].length}</span></h3>
                                        <div className="space-y-2">
                                            {groupedDrafts[cat].map((d) => {
                                                const idx = drafts.indexOf(d);
                                                return (
                                                    <div key={idx} className="bg-ink border border-white/10 rounded-sm p-3 grid sm:grid-cols-12 gap-2 items-start" data-testid={`ai-parse-row-${idx}`}>
                                                        <input value={d.name} onChange={(e) => updateDraft(idx, { name: e.target.value })}
                                                            className="sm:col-span-4 bg-ink-surface border border-white/10 rounded-sm px-2 py-1.5 text-sm text-white focus:border-violet-400/60 outline-none"
                                                            placeholder="Nome piatto" />
                                                        <input value={d.description} onChange={(e) => updateDraft(idx, { description: e.target.value })}
                                                            className="sm:col-span-4 bg-ink-surface border border-white/10 rounded-sm px-2 py-1.5 text-xs text-gray-300 focus:border-violet-400/60 outline-none"
                                                            placeholder="Descrizione" />
                                                        <div className="sm:col-span-2 flex items-center bg-ink-surface border border-white/10 rounded-sm px-2 focus-within:border-violet-400/60">
                                                            <span className="text-gold text-xs mr-1">€</span>
                                                            <input type="number" step="0.01" min="0" value={(d.price_cents / 100).toFixed(2)}
                                                                onChange={(e) => updateDraft(idx, { price_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
                                                                className="w-full bg-transparent py-1.5 text-sm text-white outline-none text-right" />
                                                        </div>
                                                        <select value={d.category} onChange={(e) => updateDraft(idx, { category: e.target.value })}
                                                            className="sm:col-span-1 bg-ink-surface border border-white/10 rounded-sm px-1 py-1.5 text-xs text-white focus:border-violet-400/60 outline-none">
                                                            {CATEGORIES_ORDER.map((c) => <option key={c} value={c}>{c}</option>)}
                                                        </select>
                                                        <button onClick={() => removeDraft(idx)} className="sm:col-span-1 text-gray-400 hover:text-red-400 p-1.5 justify-self-center" data-testid={`ai-parse-remove-${idx}`} title="Rimuovi">
                                                            <Trash2 size={14} strokeWidth={1.5} />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {drafts && (
                    <div className="border-t border-white/10 p-5 flex items-center justify-end gap-2 bg-ink">
                        <button onClick={() => setDrafts(null)} className="px-4 py-2.5 text-xs text-gray-400 hover:text-white tracking-widest uppercase" data-testid="ai-parse-restart-btn">
                            ← Ricomincia
                        </button>
                        <button onClick={confirm} disabled={submitting} data-testid="ai-parse-confirm-btn"
                            className="bg-violet-500 text-white font-semibold px-6 py-2.5 rounded-sm hover:bg-violet-400 disabled:opacity-50 tracking-widest uppercase text-xs inline-flex items-center gap-2">
                            {submitting ? <><Loader2 size={14} className="animate-spin" /> Salvataggio…</> : <><Check size={14} /> Conferma e salva {drafts.length} piatti</>}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
